import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { getPage } from "../../lib/api";
import { timestamp } from "../../lib/format";
import { frameActionGroups } from "../../lib/frames";
import type { OcppMessage } from "../../lib/types";
import { Button } from "../ui/Button";
import { ErrorNotice } from "../ui/Feedback";
import { SelectField } from "../ui/Input";
import { MicroLabel, SectionTitle } from "../ui/Text";

/**
 * The raw OCPP frames, verbatim.
 *
 * This is the view that answers "what does our charger actually send" (spec 6.4),
 * so the payload is printed exactly as stored — not normalised, not
 * pretty-relabelled, not filtered. A frame whose shape surprises us is the whole
 * reason the log exists, and reformatting it would hide the surprise.
 *
 * ## No PII to redact
 *
 * The message log stores the raw `idTag` and never an identity label (spec 9), so
 * this view is PII-free by construction rather than by filtering. A card number is
 * visible, which is correct — it is what the station sent, and diagnosing an
 * authorisation failure needs it.
 */

const PAGE_SIZE = 50;

/** Frames shown before the operator asks for the rest. */
const COLLAPSED_SIZE = 12;

export function MessageTail({
  chargePointId,
  initial,
  knownActions,
  timeZone,
}: {
  readonly chargePointId: string;
  /** The tail the detail response already carried. Avoids a second request on load. */
  readonly initial: readonly OcppMessage[];
  /** Every action the log can hold, from the server, so older frames stay filterable. */
  readonly knownActions: readonly string[];
  readonly timeZone: string;
}) {
  const [direction, setDirection] = useState("");
  const [action, setAction] = useState("");
  const [expanded, setExpanded] = useState<string | undefined>(undefined);
  const [showAll, setShowAll] = useState(false);

  const isFiltered = direction !== "" || action !== "";

  const filtered = useQuery({
    queryKey: ["messages", chargePointId, { direction, action }],
    queryFn: () =>
      getPage<OcppMessage>(`/charge-points/${encodeURIComponent(chargePointId)}/messages`, {
        query: { direction, action, limit: PAGE_SIZE },
      }),
    // Only fetched when a filter is set; unfiltered, the detail response's tail is
    // the same 50 rows and a second request for them would be waste.
    enabled: isFiltered,
  });

  const messages = isFiltered ? (filtered.data?.rows ?? []) : initial;

  /**
   * Enough to read the last exchange or two without scrolling. A filtered view is
   * never collapsed: the operator has already said what they are looking for, and
   * hiding matches behind a button would be answering a narrower question than the
   * one they asked.
   */
  const shown = showAll || isFiltered ? messages : messages.slice(0, COLLAPSED_SIZE);
  const isTruncated = shown.length < messages.length;

  /**
   * What is on screen first, because a dropdown offering thirty actions of which
   * this station has sent four is harder to use than one leading with the four.
   * The rest of the protocol follows, because the tail is only 50 frames and an
   * `Authorize` behind a run of `MeterValues` must still be findable.
   */
  const actionGroups = frameActionGroups(
    initial.map((message) => message.action),
    knownActions,
  );

  return (
    <section className="flex flex-col gap-4">
      <SectionTitle
        action={
          <MicroLabel>
            {isFiltered
              ? "Filtered"
              : isTruncated
                ? `${shown.length} of ${messages.length}`
                : `Last ${messages.length}`}
          </MicroLabel>
        }
      >
        Frames
      </SectionTitle>

      <div className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-3">
        <SelectField
          label="Direction"
          value={direction}
          onChange={(event) => setDirection(event.target.value)}
        >
          <option value="">Both</option>
          <option value="inbound">Inbound — from the station</option>
          <option value="outbound">Outbound — from this server</option>
        </SelectField>
        <SelectField
          label="Action"
          value={action}
          onChange={(event) => setAction(event.target.value)}
        >
          <option value="">Any</option>
          {actionGroups.seen.length === 0 ? null : (
            <optgroup label="Seen in recent frames">
              {actionGroups.seen.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </optgroup>
          )}
          <optgroup label="All other OCPP 1.6 actions">
            {actionGroups.other.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </optgroup>
        </SelectField>
      </div>

      {filtered.isError ? (
        <ErrorNotice error={filtered.error} onRetry={() => void filtered.refetch()} />
      ) : null}

      {messages.length === 0 ? (
        <p className="m-0 border-t border-rule pt-3 text-body text-ink-muted">
          {isFiltered ? "No frames match this filter." : "No frames recorded for this station yet."}
        </p>
      ) : (
        <>
          <ol className="m-0 flex list-none flex-col p-0">
            {shown.map((message, index) => (
              <Frame
                key={message.id}
                message={message}
                index={index}
                timeZone={timeZone}
                isExpanded={expanded === message.id}
                onToggle={() =>
                  setExpanded((current) => (current === message.id ? undefined : message.id))
                }
              />
            ))}
          </ol>

          {/*
            The tail is 50 frames, and a chatty station fills it in a couple of
            minutes — unfolded, it was two thirds of the page's scroll height and it
            pushed everything an operator came here for off the screen. The recent
            few answer "what is it saying right now"; the rest is for when that is
            not enough, so it is one press away rather than always open.
          */}
          {isTruncated ? (
            <Button variant="ghost" className="self-start" onClick={() => setShowAll(true)}>
              Show all {messages.length} frames
            </Button>
          ) : null}
        </>
      )}
    </section>
  );
}

function Frame({
  message,
  index,
  timeZone,
  isExpanded,
  onToggle,
}: {
  readonly message: OcppMessage;
  readonly index: number;
  readonly timeZone: string;
  readonly isExpanded: boolean;
  readonly onToggle: () => void;
}) {
  const inbound = message.direction === "inbound";
  const isError = message.messageType === 4;

  return (
    <li className="stage border-b border-rule" style={{ ["--stage-index" as string]: index }}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isExpanded}
        className="flex w-full items-baseline gap-3 bg-transparent px-0 py-2 text-left transition-colors duration-instant ease-swiss hover:bg-paper-sunken"
      >
        {/* Direction as a glyph plus a label. The arrow alone would be the kind of
            colour/shape-only cue this app avoids. */}
        <span
          className={`w-16 shrink-0 text-micro font-semibold uppercase tracking-[0.11em] ${
            inbound ? "text-state-charging" : "text-ink-faint"
          }`}
        >
          {inbound ? "◀ In" : "Out ▶"}
        </span>

        <span
          className={`min-w-0 flex-1 truncate text-data font-medium ${
            isError ? "text-signal" : "text-ink"
          }`}
        >
          {message.action ?? MESSAGE_TYPES[message.messageType] ?? "Frame"}
          {isError ? " · CallError" : ""}
        </span>

        <span className="shrink-0 text-micro tracking-normal normal-case text-ink-faint">
          {MESSAGE_TYPES[message.messageType] ?? `type ${message.messageType}`}
        </span>

        <span className="w-36 shrink-0 text-right text-data text-ink-faint">
          {timestamp(message.occurredAt, timeZone)}
        </span>
      </button>

      {isExpanded ? (
        <div className="flex flex-col gap-2 pb-3">
          <MicroLabel>Message id · {message.messageId}</MicroLabel>
          {/* `overflow-x: auto` on the block itself: a long frame must scroll
              inside its own box and never widen the page. */}
          <pre className="m-0 max-h-96 overflow-auto border-l-2 border-rule bg-paper-sunken px-3 py-2 text-data leading-relaxed">
            <code>{JSON.stringify(message.payload, null, 2)}</code>
          </pre>
        </div>
      ) : null}
    </li>
  );
}

/** Spec 3.1 — the OCPP-J message type ids. */
const MESSAGE_TYPES: Readonly<Record<number, string>> = {
  2: "Call",
  3: "CallResult",
  4: "CallError",
};
